import { Injectable } from '@angular/core';
import { LayerSpec, UnitSpec } from 'vega-lite/build/src/spec';
import { Field, StringFieldDef } from 'vega-lite/build/src/channeldef';
import { VariableParameter } from 'vega-lite/build/src/parameter';
import { SelectionParameter, SelectionType } from 'vega-lite/build/src/selection';
import { VisualizationSpec } from 'vega-embed';
import { TimeResolution } from '../../models/time-comparator/time-resolution';
import { TimeComparator } from '../../models/time-comparator/time-comparator';
import { TimeComparatorFactory } from '../../models/time-comparator/time-comparator-factory';
import { EstacaoTempoChuva } from '../../models/grupo-chuva-estacao';
import { MathAux } from 'src/app/instant-analysis/services/math-aux/math-aux';
import { Feature, Point } from 'geojson';

@Injectable({
  providedIn: 'root'
})
export class RainChartService {

  domainX: string[];
  domainY: number[];
  timeResolution: string = TimeResolution.ONE_HOUR;
  selectedStations: string[] = [];
  features: EstacaoTempoChuva[] = [];
  meanFieldName = 'MÉDIA DA CIDADE';

  constructor() { }

  /**
   * Inicializa o estado do serviço do gráfico de linhas.
   * @param config parâmetros de configuração, contendo os dados das chuvas, a resolução de tempo
   *    selecionada e as estações selecionadas para serem exibidas.
   */
  initialize(config: { chartData: EstacaoTempoChuva[]; timeResolution: string; selectedStations: string[]; }) {
    this.setTimeResolution(config.timeResolution);
    this.setChartData(config.chartData);
    this.setSelectedStations(config.selectedStations);
  }

  setChartData(features: EstacaoTempoChuva[]) {
    this.features = features;
  }

  setSelectedStations(selectedStations: string[]) {
    this.selectedStations = selectedStations;
  }

  setTimeResolution(timeResolution: string) {
    this.timeResolution = timeResolution;
  }

  /**
   * Cria o spec do vega-lite, que tem os dados e a configuração para exibir o gráfico.
   * 
   * @returns 
   */
  createChartSpec() {

    const { values, domainX, domainY } = this._createSpecData();
    this.domainX = domainX;
    this.domainY = domainY;

    const spec : VisualizationSpec = {
      "width": "container",
      "data": {
        "values": values,
      },
      "encoding": {
        "x": {"field": "data", "type": "temporal", "scale": {"domain": domainX}, "title": "Data"},
      },
      "layer": [
        this._createLineLayer(),
        this._createPivotVerticalRuleLayer(),
        this._createLineDotsLayer(),
      ]
    };

    return spec;
  }

  /**
   * Cria os dados para a especificação do gráfico, incluindo valores e domínios dos eixos x e y.
   * 
   * @returns dados para especificação do gráfico
   */
  private _createSpecData() {
    let props = this.filterStations();
    const map = this._calcAverageByTime("media");
    let firstDate = props[0] ? props[0].data : '';
    let lastDate = props[props.length - 1] ? props[props.length - 1].data : '';
    props = (this.selectedStations.length > 0) ? props : [];
    props.push(...map);

    let maxRain = this._getMaxRainValue(props, "media");
    maxRain = maxRain > 0 ? maxRain : 1;

    return {
      values: props,
      domainX: [firstDate, lastDate],
      domainY: [0, maxRain]
    };
  }

  /**
   * Obtém o último registro de tempo do dia para cada estação. Com isso, é possível usar o 
   * registro de 24 horas para medir a chuva total medida pela estação no dia.
   * 
   * @returns último registro de chuva de cada estação para cada dia no intervalo de tempo.
   */
  getLastRecordsPerHour() {

    let filtered = this.filterStations();
    let i = 0;
    let stationDates: EstacaoTempoChuva[] = [];
    const comparator: TimeComparator = TimeComparatorFactory.create(this.timeResolution);
    while (i < filtered.length) {

      if (! comparator.onSameInterval(filtered[i])) {
        stationDates.push(filtered[i]);
      }

      i++;
    }
    return stationDates;
  }

  /**
   * Filtra as features de chuvas, obtendo apenas aquelas cujas estações foram selecionadas pelo usuário. 
   * 
   * @returns 
   */
  filterStations() : EstacaoTempoChuva[] {

    let rainProps = this.getRainProperties();
    let selectedStations = new Set(this.selectedStations);

    let filtered = (selectedStations.size > 0) ?
      rainProps.filter(prop => selectedStations.has(prop.nome_estacao_original)) :
      rainProps;
    return filtered
  }

  /**
   * Obtém as properties associadas às features das chuvas.
   * 
   * @returns 
   */
  getRainProperties() {
    return this.features;
  }

  /**
   * Calcula a média da chuva das estações em cada instante, para o agregado fornecido.
   * @param props array de dados das chuvas das estações em diferentes instantes.
   * @param aggregate agregado de tempo de chuva
   * @returns array de valores médios de chuva em diferentes instantes
   */
  private _calcAverageByTime(aggregate: string = 'media') {

    let map : any = {};
    let props = this.getRainProperties();
    props.forEach(prop => {
      map = this._addAverageToTime(prop, map, aggregate);
    });

    return Object.keys(map).map(key => map[key]);
  }

  /**
   * Incluir o valor do registro de chuva à média da chuva no instante correspondente. 
   * 
   * @param prop registro de chuva em um dado instante
   * @param map coleção dos instantes com valores médios de chuva correspondentes
   * @param aggregate tipo do agregado de tempo de chuva usado na média
   * @returns
   */
  private _addAverageToTime(prop: any, map: any, aggregate: string = 'media' ) {
    let date = prop.data;
    if (date in map) {
      let n = map[date]['count'];
      let average = map[date][aggregate];
      map[date][aggregate] = this._calcRunningAverage(parseFloat(prop[aggregate]), average, n);
      map[date]['count']++;
    }
    else {
      map[date] = {'count': 1, 'nome_estacao_original': this.meanFieldName, 'data': date};
      map[date][aggregate] = parseFloat(prop[aggregate]);
    }
    return map;
  }

  /**
   * Calcula o valor médio com base em um novo valor e o valor médio corrente.
   * 
   * @param newValue novo valor a ser incluído na média
   * @param previousAvg média antes de adicionar o novo valor
   * @param previousCount contagem de elementos antes de adicionar o novo valor.
   * @returns nova média calculada
   */
  private _calcRunningAverage( newValue: number, previousAvg: number, previousCount: number) {

    return MathAux.round((previousAvg * previousCount + newValue) / (previousCount + 1), 4);
  }

  /**
   * Obtém o valor do maior índice de chuva no agregado solicitado.
   * 
   * @param props dados das chuvas com diferentes agregados de tempo.
   * @param aggregate 
   * @returns 
   */
  private _getMaxRainValue(props: any[], aggregate: string = 'media') {
    
    return Math.max(
      ...props.map(prop => parseFloat(prop[aggregate]))
    );
  }

  /**
   * Cria a layer do gráfico de linha dos valores de chuva.
   * A layer tem legenda, e zoom + pan no eixo das datas (x).
   * @returns objeto da layer do gráfico de linha da chuva.
   */
  private _createLineLayer(): LayerSpec<Field> | UnitSpec<Field> {
    return {
      "params": [
        this._createXZoomPanParam(),
        this._createLegendParam(),
      ],
      "mark": { "type": "line", "point": false },
      "encoding": this._createLineEncoding(),
    };
  }

  /**
   * Cria o parâmetro de zoom e pan no eixo das datas (x) do gráfico de linhas.
   * @returns parâmetro de zoom e pan no eixo x
   */
  private _createXZoomPanParam() : (VariableParameter | SelectionParameter<SelectionType>) {
    return {
      "name": "grid",
      "select": { "type": "interval", "encodings": ["x"] },
      "bind": "scales",
    };
  }

  /**
   * Cria o parâmetro da legenda interativa para o gráfico de linhas.
   * @returns parâmetro de legenda interativa.
   */
  private _createLegendParam() : (VariableParameter | SelectionParameter<SelectionType>)  {
    return {
      "name": "legenda",
      "select": { "type": "point", "fields": ["nome_estacao_original"] },
      "bind": "legend",
    }
  }

  /**
   * Cria encoding para a layer das linhas. As linhas só ficam visíveis para as estações selecionadas
   * na legenda.
   * @returns 
   */
  private _createLineEncoding() : any {
    return {
      "y": { "field": "media", "type": "quantitative", "scale": { "domain": this.domainY }, "title": "Acumulado" },
      "color": {
        "field": "nome_estacao_original",
        "type": "nominal",
        "title": "Estação",
        "scale": {
          "domain": this._getLegendLabels(),
        }
      },
      "opacity": {
        "condition": { "param": "legenda", "value": 1 },
        "value": 0,
      },
    };
  }

  /**
   * Obtém os nomes das estações, com "MÉDIA" como o primeiro elemento.
   * Usado como domínio de valores da legenda do gráfico.
   * @returns array dos nomes das estações, com "MÉDIA" como primeiro elemento.
   */
  private _getLegendLabels(): string[] {
    return [this.meanFieldName].concat(...this.selectedStations);
  }

  /**
   * Cria layer de pontos associados às linhas, com filtro pela legenda 
   * e parâmetro usado pela régua vertical.
   * @returns objeto de criação da layer de pontos.
   */
  private _createLineDotsLayer(): LayerSpec<Field> | UnitSpec<Field> {
    return {
      "mark": { "type": "point" },
      "transform": [
        { "filter": { "param": "legenda" } }
      ],
      "encoding": this._createLineDotsEncoding(),
    };
  }

  /**
   * Cria o parâmetro de hover no eixo das datas (eixo x).
   * @returns parâmetro de hover no eixo das datas.
   */
  private _createXDotsHoverParam(): VariableParameter | SelectionParameter<SelectionType> {
    return {
      "name": "index",
      "select": {
        "type": "point",
        "on": "mouseover",
        "nearest": true,
        "encodings": ["x"],
      },
    };
  }

  /**
   * Cria codificação da layer dos pontos associados às linhas. 
   * Define o tooltip exibido quando o índice do eixo das datas (eixo x) está sob um ponto.
   * @returns codificação da layer dos pontos associados às linhas
   */
  private _createLineDotsEncoding(): any {
    return {
      "y": { "field": "media", "type": "quantitative" },
      "opacity": {
        "condition": { "param": "index", "value": 1, "empty": false },
        "value": 0
      },
      "color": {
        "field": "nome_estacao_original"
      }
    };
  }

  /**
   * Cria a Layer da régua vertical sincronizada com o cursor do mouse no eixo das datas (x)
   * @returns objeto de definição da layer da régua vertical
   */
  private _createVerticalRuleLayer(): LayerSpec<Field> | UnitSpec<Field> {
    return {
      "mark": "rule",
      "transform": [
        {
          "filter": {
            "and": ["index.data", { "param": "index" }]
          }
        }
      ]
    };
  }

  /**
   * Cria a Layer da régua vertical sincronizada com o cursor do mouse no eixo das datas (x).
   * Esta régua vertical é capaz de distinguir os valores de chuva (y) das estações em cada data (x).
   * Isso é alcançado por meio da transformação pivot com groupby.
   * @see https://observablehq.com/@vega/multi-series-line-chart-with-tooltip
   * @returns objeto de definição da layer da régua vertical
   */
  private _createPivotVerticalRuleLayer(): LayerSpec<Field> | UnitSpec<Field> {
    return {
      "mark": "rule",
      "params": [
        this._createXDotsHoverParam(),
      ],
      "transform": [
        {
          "pivot": "nome_estacao_original", "value": "media", "groupby": ["data"]
        }
      ],
      "encoding": {
        "opacity": {
          "condition": {"param": "index", "value": 0.4, "empty": false},
          "value": 0
        },
        "tooltip": [
          { "field": "data", "type": "temporal", "timeUnit": "utcyearmonthdatehoursminutes", "title": "Data" },
          {"field": this.meanFieldName, "type": "quantitative"},
          ...this.selectedStations.map((station: string): StringFieldDef<Field> => {
            return {"field": station, "type": "quantitative"}
          })
        ],
      }
    };
  }
}
